import type {
  DeviceChangeType,
  SoftphoneEvent,
} from 'Modules/softphone/services/EventService';

/** One change, placed on the shared time axis. */
export interface DeviceLaneMark {
  event: SoftphoneEvent;
  /** 0-100, where this change sits between the window's two edges. */
  offsetPercent: number;
}

export interface DeviceLane {
  type: DeviceChangeType;
  marks: DeviceLaneMark[];
}

export interface DeviceLaneModel {
  /** Always all three lanes, in a fixed order — an empty one is a reading too. */
  lanes: DeviceLane[];
  /** ISO bounds of the axis, for the two labels above it. */
  windowStart: string;
  windowEnd: string;
  /**
   * Changes that carry no recognisable `deviceType` and so belong to no lane.
   *
   * Counted rather than dropped silently: lanes summing to less than the
   * record's change count would read as "that is all of them".
   */
  unplacedCount: number;
}

export interface DeviceLaneBounds {
  startTime?: string | null;
  endTime?: string | null;
  /** The right edge of a call still running. Passed in, so the model is pure. */
  nowMs: number;
}

const LANE_ORDER: DeviceChangeType[] = ['microphone', 'speaker', 'ringer'];

const toMs = (value: string | null | undefined): number | null => {
  if (!value) {
    return null;
  }

  const ms = new Date(value).getTime();

  return Number.isNaN(ms) ? null : ms;
};

const isDeviceChangeType = (value: unknown): value is DeviceChangeType =>
  LANE_ORDER.includes(value as DeviceChangeType);

/**
 * Which device a change is about, or null when the record cannot say.
 *
 * Read from `details.deviceType` rather than parsed out of the message: the
 * message is prose written for a human, and an older record may word it
 * differently than today's does.
 */
export const getDeviceChangeType = (
  event: SoftphoneEvent
): DeviceChangeType | null => {
  const type: unknown = event.details?.deviceType;

  return isDeviceChangeType(type) ? type : null;
};

const clampPercent = (value: number): number =>
  Math.round(Math.min(100, Math.max(0, value)) * 100) / 100;

/**
 * Lays device changes out on one time axis, one lane per device kind.
 *
 * The point of the lanes over the list is elapsed time: three switches inside
 * four seconds and three spread over the call read identically in a list, and
 * the first one is a device flapping. So the marks are placed on real time
 * rather than spaced evenly, and a cluster is left to overlap — that clump is
 * the finding, not a collision to resolve.
 *
 * A call still running has no `endTime`; its axis ends at `nowMs`, so the last
 * mark drifts left as the call goes on instead of being pinned to the edge.
 */
export const buildDeviceLanes = (
  changes: SoftphoneEvent[],
  { startTime, endTime, nowMs }: DeviceLaneBounds
): DeviceLaneModel => {
  const placed = changes
    .map((event) => ({
      event,
      type: getDeviceChangeType(event),
      ms: toMs(event.timestamp),
    }))
    .filter(
      (entry): entry is { event: SoftphoneEvent; type: DeviceChangeType; ms: number } =>
        entry.type !== null && entry.ms !== null
    )
    .sort((a, b) => a.ms - b.ms);

  const stamps = placed.map((entry) => entry.ms);
  const startMs = toMs(startTime) ?? (stamps.length > 0 ? stamps[0] : nowMs);
  const endMs = toMs(endTime) ?? Math.max(nowMs, startMs, ...stamps);

  /* A record whose window collapsed to an instant still has to divide by
     something; every mark then lands honestly on the left edge. */
  const span = Math.max(endMs - startMs, 1);

  return {
    lanes: LANE_ORDER.map((type) => ({
      type,
      marks: placed
        .filter((entry) => entry.type === type)
        .map(({ event, ms }) => ({
          event,
          offsetPercent: clampPercent(((ms - startMs) / span) * 100),
        })),
    })),
    windowStart: new Date(startMs).toISOString(),
    windowEnd: new Date(endMs).toISOString(),
    unplacedCount: changes.length - placed.length,
  };
};
